import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  IBatch,
  IBatchDelivery,
  IBatchRaw,
  IDriverLocation,
  IDriverProfile,
  IOrganization,
  IUser,
} from "../types/types";
import { EXHUT_DELIVERY_LOCAL_STORAGE_TOKEN } from "../utils/constants";
import { getAuthUrl, getBackendUrl } from "../helpers/env.helper";

export function useGetDeliveryProfile() {
  return _useGet<IDriverProfile>(`batch-delivery/profile`, ["profile"], true);
}

export function useGetDriverLocation(
  lat?: number,
  lng?: number,
  zipcode?: string,
) {
  const params = [];
  if (lat) {
    params.push(`lat=${lat}`);
  }
  if (lng) {
    params.push(`lng=${lng}`);
  }
  if (zipcode) {
    params.push(`zipcode=${zipcode}`);
  }
  return _useGet<IDriverLocation>(
    `batch-delivery/location?${params.join("&")}`,
    ["location", `${lat}`, `${lng}`, zipcode],
  );
}

export function useGetBatches(lat?: number, lng?: number) {
  return _useGet<IBatchRaw[]>(
    `batch-delivery/batches?lat=${lat}&lng=${lng}`,
    ["batches", `${lat}`, `${lng}`],
    !!lat && !!lng,
  );
}

export function useGetBatchById(id?: string) {
  return _useGet<IBatch>(`batch-delivery/batches/${id}`, ["batches", id], !!id);
}

export function useGetDeliveriesForBatch(batchId?: string) {
  return _useGet<IBatchDelivery[]>(
    `batch-delivery/batches/${batchId}/deliveries`,
    ["batches", batchId, "deliveries"],
    !!batchId,
  );
}

export function useGetOrganizationById(id?: string) {
  return _useGet<IOrganization>(
    `organizations/${id}`,
    ["organizations" + id],
    !!id,
  );
}

export function useGetSession(enabled: boolean) {
  return _useGetAuthBE<IUser>("session", ["session"], enabled);
}

function _useGetAuthBE<T1>(
  path: string,
  keys: (string | undefined | null)[],
  enabled?: boolean,
  authenticated: boolean = true,
) {
  return _useGet<T1>(path, keys, enabled, authenticated, getAuthUrl());
}

function _useGet<T1>(
  path: string,
  keys: (string | undefined | null)[],
  enabled?: boolean,
  authenticated: boolean = true,
  host?: string,
) {
  let headers: any = {};

  const hostUrl = host ?? getBackendUrl();

  if (authenticated) {
    headers["authorization"] = localStorage.getItem(
      EXHUT_DELIVERY_LOCAL_STORAGE_TOKEN,
    );
  }

  return useQuery<T1, Error>({
    queryFn: async () => {
      return axios
        .get(`${hostUrl}/${path}`, {
          headers,
        })
        .then((resp) => resp.data);
    },
    queryKey: keys,
    enabled,
    retry: 0,
  });
}
